<template>
  <BaseModal
    :is-open="isOpen"
    :title="modalTitle"
    max-width="500px"
    @close="handleClose"
  >
    <div class="player-left-container">
      <!-- Player Info -->
      <div class="player-info">
        <div class="status-icon" :class="{ temporary: isTemporary }">
          <span class="material-symbols-outlined">{{ statusIcon }}</span>
        </div>

        <div class="player-details">
          <h3 class="player-name">{{ playerData?.player_name || 'A player' }}</h3>
          <p class="status-message">{{ statusMessage }}</p>

          <div v-if="playerData?.seat !== undefined" class="seat-info">
            <span class="material-symbols-outlined">event_seat</span>
            <span>Seat {{ playerData.seat + 1 }} ({{ getTeamLabel(playerData.seat) }})</span>
          </div>
        </div>
      </div>

      <!-- Reconnection Timer (if temporary) -->
      <div v-if="isTemporary && timeRemaining > 0" class="reconnect-timer">
        <div class="timer-header">
          <span class="material-symbols-outlined">schedule</span>
          <span>Reconnection Window</span>
        </div>
        <div class="timer-bar-container">
          <div class="timer-bar" :style="{ width: `${timerProgress}%` }"></div>
        </div>
        <p class="timer-text">{{ formatTime(timeRemaining) }} remaining</p>
      </div>

      <!-- Replacement Options (if permanent or timer expired) -->
      <div v-else-if="!isTemporary || timeRemaining <= 0" class="replacement-options">
        <p class="options-prompt">Would you like to replace this player?</p>
        <div class="option-buttons">
          <button @click="handleReplaceWithBot" class="btn-option">
            <span class="material-symbols-outlined">smart_toy</span>
            <span>Replace with Bot</span>
          </button>
          <button @click="handleLeaveOpen" class="btn-option">
            <span class="material-symbols-outlined">person_add</span>
            <span>Leave Open for New Player</span>
          </button>
        </div>
      </div>

      <!-- Close Button -->
      <button @click="handleClose" class="btn-close">
        <span>{{ isTemporary ? 'Continue Game' : 'Close' }}</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * PlayerLeftModal Component
 *
 * Notifies when a player disconnects from the game.
 * Shows different UI based on temporary vs permanent disconnect.
 *
 * Props:
 * - player_name: Name of player who left
 * - seat: Player's seat (0-3)
 * - temporarily: Whether disconnect is temporary (will wait for reconnect)
 * - reconnect_timeout: Time in seconds to wait for reconnection
 *
 * Emits:
 * - replace_with_bot: { seat: number } - Replace with bot
 * - leave_open: { seat: number } - Leave seat open
 * - close: [] - Modal closed
 */

interface Props {
  isOpen: boolean
  data: {
    player_name?: string
    seat?: number
    temporarily?: boolean
    reconnect_timeout?: number // seconds
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  replace_with_bot: [{ seat: number }]
  leave_open: [{ seat: number }]
  close: []
}>()

/**
 * Player data
 */
const playerData = computed(() => props.data)
const isTemporary = computed(() => props.data?.temporarily ?? false)

/**
 * Reconnection timer
 */
const timeRemaining = ref(props.data?.reconnect_timeout || 120) // default 2 minutes
const timerInterval = ref<number | null>(null)

/**
 * Start countdown timer
 */
function startTimer(): void {
  if (timerInterval.value) clearInterval(timerInterval.value)

  timerInterval.value = window.setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value -= 1
    } else {
      if (timerInterval.value) clearInterval(timerInterval.value)
    }
  }, 1000)
}

/**
 * Stop timer
 */
function stopTimer(): void {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

/**
 * Watch for modal open to start timer
 */
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen && isTemporary.value) {
      timeRemaining.value = props.data?.reconnect_timeout || 120
      startTimer()
    } else {
      stopTimer()
    }
  }
)

/**
 * Cleanup timer on unmount
 */
onUnmounted(() => {
  stopTimer()
})

/**
 * Modal title
 */
const modalTitle = computed(() => {
  if (isTemporary.value) return 'Player Disconnected'
  return 'Player Left Game'
})

/**
 * Status icon
 */
const statusIcon = computed(() => {
  if (isTemporary.value) return 'wifi_off'
  return 'exit_to_app'
})

/**
 * Status message
 */
const statusMessage = computed(() => {
  if (isTemporary.value) {
    return 'has temporarily disconnected. They can reconnect within the time window.'
  }
  return 'has permanently left the game.'
})

/**
 * Timer progress percentage
 */
const timerProgress = computed(() => {
  const total = props.data?.reconnect_timeout || 120
  return (timeRemaining.value / total) * 100
})

/**
 * Get team label for seat
 */
function getTeamLabel(seat: number): string {
  return seat % 2 === 0 ? 'Team A' : 'Team B'
}

/**
 * Format time (MM:SS)
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Handle replace with bot
 */
function handleReplaceWithBot(): void {
  if (playerData.value?.seat !== undefined) {
    emit('replace_with_bot', { seat: playerData.value.seat })
  }
  handleClose()
}

/**
 * Handle leave open
 */
function handleLeaveOpen(): void {
  if (playerData.value?.seat !== undefined) {
    emit('leave_open', { seat: playerData.value.seat })
  }
  handleClose()
}

/**
 * Handle close
 */
function handleClose(): void {
  stopTimer()
  emit('close')
}
</script>

<style scoped>
.player-left-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Player Info */
.player-info {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.status-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-icon.temporary {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
}

.status-icon .material-symbols-outlined {
  font-size: 2rem;
  color: #f87171;
}

.status-icon.temporary .material-symbols-outlined {
  color: #fbbf24;
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.player-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.status-message {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0;
}

.seat-info {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  width: fit-content;
  margin-top: 0.25rem;
}

.seat-info .material-symbols-outlined {
  font-size: 1rem;
}

/* Reconnect Timer */
.reconnect-timer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
}

.timer-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fbbf24;
}

.timer-header .material-symbols-outlined {
  font-size: 1.125rem;
}

.timer-bar-container {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.timer-bar {
  height: 100%;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  border-radius: 4px;
  transition: width 1s linear;
}

.timer-text {
  font-size: 1.125rem;
  font-weight: 700;
  color: #fbbf24;
  text-align: center;
  margin: 0;
  font-variant-numeric: tabular-nums;
}

/* Replacement Options */
.replacement-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.options-prompt {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin: 0;
}

.option-buttons {
  display: flex;
  gap: 0.75rem;
}

.btn-option {
  flex: 1;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.9);
}

.btn-option:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-option .material-symbols-outlined {
  font-size: 2rem;
  color: #e8c547;
}

/* Close Button */
.btn-close {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  margin-top: 0.5rem;
}

.btn-close:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-close:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

/* Responsive */
@media (max-width: 640px) {
  .player-info {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .player-details {
    align-items: center;
  }

  .seat-info {
    margin: 0 auto;
  }

  .option-buttons {
    flex-direction: column;
  }
}
</style>
